import type { StampObject as MapStampObject } from '../../../../common/types';
import * as ImageCache from '../../ImageCache';
import CanvasObject from '../CanvasObject';
import * as geometry from '../common/geometry';
import type SurfaceBase from '../SurfaceBase';
import { getStampImageUrl } from './common';

export default class StampObject extends CanvasObject {
  stamp: MapStampObject;
  imageCanvas: CanvasImageSource | null = null;
  emojiCanvas: CanvasImageSource | null = null;

  constructor(id: string, surface: SurfaceBase, stamp: MapStampObject) {
    super(id, surface);

    this.stamp = stamp;

    if (stamp.properties.subtype !== 'emoji') {
      ImageCache.getImage(getStampImageUrl(stamp.properties.path)).then((imageCanvas) => {
        this.imageCanvas = imageCanvas;
        this.renderAll();
      });
    }

    // initlize emoji to an image canvas (helps with rendering);
    if (this.isEmoji()) {
      const [p1, p2, p3, p4] = this.stamp.geometry.coordinates[0]
        .slice(0, -1)
        .map(([lng, lat]) => this.surface.toScreenCoords({ lng, lat }));

      const w = geometry.distance(p1, p2);
      const h = geometry.distance(p2, p3);
      const fontSize = h;

      const canvas = new OffscreenCanvas(w, h);
      const ctx = canvas.getContext('2d')!;
      ctx.font = `${fontSize}px Arial`;
      const dims = ctx?.measureText(stamp.properties.emoji!);
      const offset = dims.fontBoundingBoxDescent / 2 - 1;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(stamp.properties.emoji!, w / 2, h / 2 + offset);
      this.emojiCanvas = canvas;
    }
  }

  isEmoji = () => {
    return this.stamp.properties.subtype === 'emoji' && this.stamp.properties.emoji !== undefined;
  };

  render(ctx: CanvasRenderingContext2D) {
    if (!this.stamp) return;
    const { properties, description } = this.stamp;
    const [p1, p2, p3, p4] = this.stamp.geometry.coordinates[0]
      .slice(0, -1)
      .map(([lng, lat]) => this.surface.toScreenCoords({ lng, lat }));

    const angleInRadians = geometry.lineAngle(p1, p2);
    const width = geometry.distance(p1, p2);
    const height = geometry.distance(p2, p3);
    const center = geometry.polygonCentroid([p1, p2, p3, p4]);

    if (this.isEmoji() && this.emojiCanvas) {
      ctx.save();
      ctx.translate(center.x, center.y);
      ctx.rotate(angleInRadians);
      ctx.translate(-center.x, -center.y);
      ctx.globalAlpha = properties.fillOpacity;
      ctx.drawImage(this.emojiCanvas, center.x - width / 2, center.y - height / 2, width, height);
      ctx.restore();
      return;
    }

    if (this.imageCanvas) {
      ctx.save();
      ctx.translate(center.x, center.y);
      ctx.rotate(angleInRadians);
      ctx.translate(-center.x, -center.y);

      ctx.globalAlpha = properties.fillOpacity;
      ctx.drawImage(this.imageCanvas, center.x - width / 2, center.y - height / 2, width, height);

      ctx.restore();
      ctx.globalAlpha = 1;

      this.surface.resetCanvasTransform(ctx);
    }
  }

  drawHitArea(ctx: OffscreenCanvasRenderingContext2D, color: string): void {
    if (this.stamp) {
      const [p1, p2, p3, p4] = this.stamp.geometry.coordinates[0]
        .slice(0, -1)
        .map(([lng, lat]) => this.surface.toScreenCoords({ lng, lat }));

      ctx.beginPath();
      ctx.fillStyle = color;

      ctx.moveTo(p1.x, p1.y);
      ctx.lineTo(p2.x, p2.y);
      ctx.lineTo(p3.x, p3.y);
      ctx.lineTo(p4.x, p4.y);
      ctx.fill();
    }
  }
}
